/**
 * Idle Manager Utility in InGame state
 * 
 * Provides a centralized system for managing processes that should start or stop
 * depending on the application's idle state when ingame mode is on.
 * 
 * FEATURES:
 * - Manages multiple processes with unique IDs
 * - Automatically starts/stops processes based on idle state
 * - Ensures safe start/stop operations with error handling
 * - Provides a React hook (`useIdleManager`) for easy integration in components
 * - Syncs with global `useIsIdle` store state
 */

import { useIsIdle } from "../../../store/app/appStore";
import { useCallback, useEffect } from "react";

interface ProcessConfig {
    id: string;
    startFn: () => void;
    stopFn: () => void;
    isActive: boolean;
}

class IdleManager {
    private processes = new Map<string, ProcessConfig>();
    private isIdle = false;

    constructor() { }

    public handleIdleStateChange(isIdle: boolean) {
        this.isIdle = isIdle;
        this.processes.forEach((process) => { isIdle ? this.safeStop(process) : this.safeStart(process); });
    };

    private safeStart = (process: ProcessConfig) => {
        if (process.isActive) return;
        process.startFn();
        process.isActive = true;
    };

    private safeStop = (process: ProcessConfig) => {
        if (!process.isActive) return;
        process.stopFn();
        process.isActive = false;
    };


    public registerProcess(id: string, startFn: () => void, stopFn: () => void): void {

        const process: ProcessConfig = { id, startFn, stopFn, isActive: false };
        this.processes.set(id, process);

        // If not idle, start the process immediately
        if (!this.isIdle) {
            try {
                this.safeStart(process);
            } catch (error) {
                console.warn(`[IdleManager] Error starting process ${id}:`, error);
            };
        };
    };

    public unregisterProcess(id: string): void {
        const process = this.processes.get(id);
        if (!process) return;

        try {
            this.safeStop(process);
            this.processes.delete(id)
        } catch (error) {
            console.warn(`[IdleManager] Error stopping process ${id}:`, error);
        };
    };

}

export const idleManager = new IdleManager();

// Hook for easier usage in React components
export const useIdleManager = () => {
    const isIdle = useIsIdle();

    const registerProcess = useCallback((id: string, startFn: () => void, stopFn: () => void) => {
        idleManager.registerProcess(id, startFn, stopFn);
    }, []);

    const unregisterProcess = useCallback((id: string) => {
        idleManager.unregisterProcess(id);
    }, []);

    useEffect(() => {
        idleManager.handleIdleStateChange(isIdle);
    }, [isIdle]);

    return {
        isIdle,
        registerProcess,
        unregisterProcess,
    };
};
